import React, {useState} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  Alert,
  Snackbar
} from "@mui/material";
import {AddCircle, RemoveCircle} from "@mui/icons-material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import properties from "constants/ApplicationConstants";
import {POST} from "APIclient";
import {useNavigate, useLocation} from "react-router-dom";

const AddSkills = ({title = "Add Skills"}) => {
  const navigate = useNavigate();

  const [skills, setSkills] = useState([{skillSet: "", skill_type: ""}]);

  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [loading, setLoading] = useState(false);

  const skillTypes = {
    1: "technical-primary",
    2: "technical-secondary",
    3: "competency-primary",
    4: "competency-secondary",
    5: "questionnaire"
  };

  const showSnack = (msg, color = "success") => {
    setSnackMessage(msg);
    setSnackColor(color);
    setSnackOpen(true);
  };
  const handleCloseSnack = () => setSnackOpen(false);

  const handleSkillChange = (idx, field, value) => {
    const updated = [...skills];
    updated[idx][field] = value;
    setSkills(updated);

    if (errors[idx]) {
      setErrors(prev => {
        const copy = {...prev};
        delete copy[idx][field];
        if (Object.keys(copy[idx]).length === 0) delete copy[idx];
        return copy;
      });
    }
  };

  const addRow = () => setSkills([...skills, {skillSet: "", skill_type: ""}]);

  const removeRow = idx => {
    if (skills.length === 1) return;
    setSkills(skills.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const newErrors = {};
    skills.forEach((s, i) => {
      if (!s.skillSet.trim())
        (newErrors[i] = newErrors[i] || {}).skillSet = "Skill Set is required";
      if (!s.skill_type) (newErrors[i] = newErrors[i] || {}).skill_type = "Skill Type is required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        skillSet: skills.map(s => s.skillSet.trim()),
        skillTypes: skills.map(s => skillTypes[Number(s.skill_type)])
      };

      const url = "admin/appraisal-form/add-skills";
      const response = await POST(url, payload);

      if (response?.status === 200) {
        showSnack(response.data?.data?.message || "Skills added successfully!", "success");
        // setTimeout(() => navigate("/admin/account/index"), 1500);
        setSkills([{skillSet: "", skill_type: ""}]);
        setErrors({});
      }
    } catch (error) {
      const msg = error?.response?.data?.message || err?.message || "Failed to save skills";
      showSnack(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={1}>
        <Box sx={{flexGrow: 1, p: 2}}>
          <Paper elevation={3}>
            <Box
              sx={{
                background: properties.colors.bgcolorbluethem,
                color: "white",
                p: 2
              }}
            >
              <Typography
                variant="h6"
                fontSize={15}
                sx={{color: properties.colors.White, fontFamily: properties.fontFamily}}
              >
                {title}
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{p: 3}}>
              <Grid container spacing={2}>
                <Grid container spacing={1} alignItems="center" mt={1}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      fontWeight="bold"
                      variant="subtitle2"
                      fontFamily={properties.fontFamily}
                    >
                      Skill Set / Questionnaire
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      fontWeight="bold"
                      variant="subtitle2"
                      fontFamily={properties.fontFamily}
                    >
                      Skill Types
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2} />
                </Grid>

                {skills.map((skill, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter Skill Set / Questionnaire"
                          value={skill.skillSet}
                          onChange={e => handleSkillChange(idx, "skillSet", e.target.value)}
                          error={!!errors[idx]?.skillSet}
                          helperText={errors[idx]?.skillSet || " "}
                          sx={{"& .MuiInputBase-root": {height: 50}}}
                          InputLabelProps={{sx: {fontSize: 13}}}
                          inputProps={{style: {fontSize: 15}}}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Select"
                          value={skill.skill_type}
                          onChange={e => handleSkillChange(idx, "skill_type", e.target.value)}
                          error={!!errors[idx]?.skill_type}
                          helperText={errors[idx]?.skill_type || " "}
                          sx={{
                            "& .MuiInputBase-root": {height: 50},
                            "& .MuiSelect-select": {fontSize: 14}
                          }}
                          InputLabelProps={{sx: {fontSize: 13}}}
                        >
                          <MenuItem value="" disabled>
                            Select Skill Type
                          </MenuItem>
                          {Object.entries(skillTypes).map(([k, v]) => (
                            <MenuItem key={k} value={k}>
                              {v
                                .split("-")
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join("-")}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={2} sx={{display: "flex", alignItems: "center"}}>
                        <IconButton
                          color="error"
                          onClick={() => removeRow(idx)}
                          disabled={skills.length === 1}
                        >
                          <RemoveCircle />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}

                <Grid item xs={12} sx={{mt: 1}}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<AddCircle />}
                    onClick={addRow}
                    sx={{fontFamily: properties.fontFamily}}
                  >
                    Add More..
                  </MDButton>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 4,
                  textAlign: "right",
                  bgcolor: "grey.100",
                  p: 2,
                  borderRadius: 1
                }}
              >
                <MDButton
                  variant="gradient"
                  color="warning"
                  sx={{width: "15ch", mr: 2, fontFamily: properties.fontFamily}}
                  onClick={() => navigate(-1)}
                >
                  Back
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  sx={{
                    width: "20ch",
                    background: properties.colors.bgcolorbluethem,
                    fontFamily: properties.fontFamily,
                    "&:hover": {background: properties.colors.bgcolorbluethem}
                  }}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </MDButton>
              </Box>
            </Box>
          </Paper>
        </Box>
      </MDBox>

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnack}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={snackColor}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default AddSkills;
